import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import HoldbackCalendar from "@/components/dashboard/HoldbackCalendar";
import { Layers, Link2, MapPin, DollarSign, Search, Receipt, Trash2, CheckCircle2, Archive, Plus, X, CalendarRange, History } from "lucide-react";
import ProjectDetail from "@/components/dashboard/ProjectDetail";
import { CustomerPicker } from "@/components/dashboard/CustomerPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/hooks/useConfirm";

const statusStyle = (s) => {
  const map = {
    Active: "bg-[#1a3fd1]/15 text-[#8fa4f0] border-[#1a3fd1]/40",
    Inactive: "bg-white/10 text-white/50 border-white/20",
    "In Progress": "bg-amber-500/15 text-amber-300 border-amber-500/40",
    Completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    "On Hold": "bg-white/10 text-white/50 border-white/20",
  };
  return map[s] || "bg-white/10 text-white/60 border-white/20";
};

// Legacy statuses collapse to Active/Inactive for the inline picker.
const normalizedStatus = (s) => {
  if (s === "Inactive" || s === "Completed" || s === "On Hold") return "Inactive";
  return "Active";
};

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const SWIPE_REVEAL = 132; // px of shortcut strip revealed by swiping left (phone only)

/** Phone-only swipe-left wrapper: drags the row aside to reveal
 *  Activate/Deactivate + Delete shortcuts underneath. Desktop is untouched
 *  (md:contents makes the wrappers layout-neutral). Vertical scrolling
 *  always wins over the horizontal gesture. */
const SwipeRow = ({ id, openId, setOpenId, isActive, onToggleStatus, onDelete, children }) => {
  const start = useRef(null);
  const dragRef = useRef(null);          // ref mirror — touchmove is a "continuous" React
  const [drag, setDragState] = useState(null); // event and batches; the ref is always current
  const setDrag = (v) => { dragRef.current = v; setDragState(v); };
  const open = openId === id;
  const offset = drag !== null ? drag : open ? -SWIPE_REVEAL : 0;

  const onTouchStart = (e) => {
    start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, base: open ? -SWIPE_REVEAL : 0 };
  };
  const onTouchMove = (e) => {
    if (!start.current) return;
    const dx = e.touches[0].clientX - start.current.x;
    const dy = e.touches[0].clientY - start.current.y;
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;      // intent deadzone
    if (Math.abs(dy) > Math.abs(dx)) { start.current = null; setDrag(null); return; } // scroll wins
    setDrag(Math.max(-SWIPE_REVEAL, Math.min(0, start.current.base + dx)));
  };
  const onTouchEnd = () => {
    if (!start.current) return;
    const final = dragRef.current !== null ? dragRef.current : start.current.base;
    setOpenId(final < -SWIPE_REVEAL / 2 ? id : null);
    start.current = null;
    setDrag(null);
  };

  return (
    <div className="relative md:contents">
      {/* shortcut strip behind the row — phone only */}
      <div className="absolute inset-y-0 right-0 flex w-[132px] overflow-hidden rounded-r-sm md:hidden" aria-hidden={!open}>
        <button
          type="button"
          tabIndex={open ? 0 : -1}
          onClick={() => { setOpenId(null); onToggleStatus(); }}
          data-testid={`swipe-status-${id}`}
          className="flex flex-1 flex-col items-center justify-center gap-1 bg-[#1a3fd1] font-mono text-[8px] font-bold uppercase tracking-wider text-white"
        >
          {isActive ? <Archive className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {isActive ? "Inactivate" : "Activate"}
        </button>
        <button
          type="button"
          tabIndex={open ? 0 : -1}
          onClick={() => { setOpenId(null); onDelete(); }}
          data-testid={`swipe-delete-${id}`}
          className="flex flex-1 flex-col items-center justify-center gap-1 bg-red-600 font-mono text-[8px] font-bold uppercase tracking-wider text-white"
        >
          <Trash2 className="h-4 w-4" /> Delete
        </button>
      </div>
      <div
        className="relative bg-[#0a0a0a] md:contents"
        style={{ transform: offset ? `translateX(${offset}px)` : undefined, transition: drag === null ? "transform .18s ease-out" : "none" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClickCapture={open ? (e) => { e.preventDefault(); e.stopPropagation(); setOpenId(null); } : undefined}
        data-testid={`swipe-row-${id}`}
      >
        {children}
      </div>
    </div>
  );
};


const ProjectsTab = ({ openProjectId, onProjectConsumed }) => {
  const [showHoldbacks, setShowHoldbacks] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("project") || null;

  // Record opens into the recent-jobs strip (covers row taps AND deep links,
  // since every path sets the ?project= param).
  useEffect(() => {
    if (!selectedId) return;
    setRecents((prev) => {
      const p = projects.find((x) => x.project_id === selectedId);
      const entry = {
        id: selectedId,
        name: (p?.name || prev.find((r) => r.id === selectedId)?.name || "Project"),
        customer: (p?.customer || p?.customer_name || prev.find((r) => r.id === selectedId)?.customer || ""),
      };
      const next = [entry, ...prev.filter((r) => r.id !== selectedId)].slice(0, 3);
      try { localStorage.setItem("em_recent_projects", JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, [selectedId, projects]);
  const [query, setQuery] = useState("");
  const [checked, setChecked] = useState(() => new Set());
  const [openSwipeId, setOpenSwipeId] = useState(null); // phone: which row's swipe shortcuts are revealed
  // Last 3 opened projects (this device) — quick-access chips above the list.
  const [recents, setRecents] = useState(() => {
    try { return JSON.parse(localStorage.getItem("em_recent_projects") || "[]"); } catch { return []; }
  });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [customerOpts, setCustomerOpts] = useState([]); // for the new-project picker

  // Load customer options when the create dialog opens
  useEffect(() => {
    if (!creating) return;
    api.get("/customers").then((r) => setCustomerOpts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [creating]);
  const WORK_TYPES = ["Thermal Insulation", "Ductwork Insulation", "Firestopping", "Firewrapping"];
  const emptyNew = { name: "", customer: "", location: "", value: "", contract_number: "", date: "", work_types: [], type_values: {}, notes: "" };
  const [newForm, setNewForm] = useState(emptyNew);
  const [saving, setSaving] = useState(false);
  // Segmented view: active projects (default) or inactive/archived ones.
  const [statusView, setStatusView] = useState(() => localStorage.getItem("projects-status-view") || "active");
  useEffect(() => { localStorage.setItem("projects-status-view", statusView); }, [statusView]);
  // Reset the bulk selection whenever the user toggles between Active / Inactive
  // — carrying selections across the two lists would be confusing.
  useEffect(() => { setChecked(new Set()); }, [statusView]);

  const setSelectedId = useCallback(
    (id) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (id) p.set("project", id);
          else p.delete("project");
          return p;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  // Owes chips deep-link into Billing → Who-owes-what focused on the customer.
  const openReceivable = useCallback(
    (customer) => {
      if (!customer) return;
      setSearchParams({ tab: "billing", receivable: customer });
    },
    [setSearchParams]
  );

  const filtered = useMemo(() => {
    const byStatus = projects.filter((p) => normalizedStatus(p.status) === (statusView === "active" ? "Active" : "Inactive"));
    const q = query.trim().toLowerCase();
    if (!q) return byStatus;
    const terms = q.split(/\s+/);
    return byStatus.filter((p) => {
      const lines = Array.isArray(p.contract_lines)
        ? p.contract_lines.map((l) => `${l.description || ""} ${l.code || ""}`).join(" ")
        : "";
      const hay = [
        p.name, p.customer, p.customer_name, p.location, p.address, p.status,
        p.po, p.progress_claim_no, p.date, p.notes, lines,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [projects, query, statusView]);

  const statusCounts = useMemo(() => {
    let active = 0, inactive = 0;
    for (const p of projects) {
      if (normalizedStatus(p.status) === "Active") active += 1; else inactive += 1;
    }
    return { active, inactive };
  }, [projects]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/projects");
      setProjects(r.data);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirm = useConfirm();

  const changeStatus = async (project, next) => {
    if (next === project.status) return;
    // Optimistic update
    setProjects((prev) => prev.map((p) => (p.project_id === project.project_id ? { ...p, status: next } : p)));
    try {
      await api.put(`/projects/${project.project_id}`, { status: next });
    } catch {
      toast.error("Could not update status");
      load();
    }
  };

  const createProject = async () => {
    if (!newForm.name.trim()) {
      toast.error("Give the project a name");
      return;
    }
    setSaving(true);
    try {
      // Build contract lines from selected work types so each becomes an
      // editable PO line the user can bill against.
      const contract_lines = (newForm.work_types || []).map((t) => ({
        line_id: Math.random().toString(36).slice(2),
        item: t,
        description: t,
        contract_value: Number(newForm.type_values?.[t]) || 0,
        value_to_date: 0,
        percent_to_date: 0,
        estimate_number: "",
      }));
      const { data } = await api.post("/projects", {
        name: newForm.name.trim(),
        customer: newForm.customer.trim(),
        location: newForm.location.trim(),
        value: Number(newForm.value) || 0,
        po: newForm.contract_number.trim(),
        date: newForm.date || "",
        status: "Active",
        contract_lines,
        notes: (newForm.notes || "").trim(),
      });
      toast.success("Project created");
      setCreating(false);
      setNewForm(emptyNew);
      setProjects((prev) => [data, ...prev]);
      setSelectedId(data.project_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create project");
    } finally {
      setSaving(false);
    }
  };


  const bulkChangeStatus = async (next) => {
    const ids = Array.from(checked);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Mark ${ids.length} project${ids.length === 1 ? "" : "s"} as ${next}?`,
      description: `The selected project${ids.length === 1 ? " will move" : "s will move"} to the ${next.toLowerCase()} list.`,
      confirmLabel: `Set ${next}`,
    });
    if (!ok) return;
    setBulkBusy(true);
    // Optimistic update — flip statuses locally first so the UI feels snappy.
    setProjects((prev) => prev.map((p) => (checked.has(p.project_id) ? { ...p, status: next } : p)));
    try {
      await Promise.all(ids.map((id) => api.put(`/projects/${id}`, { status: next })));
      toast.success(`${ids.length} project${ids.length === 1 ? "" : "s"} moved to ${next}`);
      setChecked(new Set());
    } catch {
      toast.error("Could not update every project");
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  const deleteProject = async (project) => {
    const ok = await confirm({
      title: `Delete "${project.name}"?`,
      description: "This will permanently remove the project and its billing lines. This cannot be undone.",
      confirmLabel: "Delete project",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/projects/${project.project_id}`);
      toast.success("Project deleted");
      setProjects((prev) => prev.filter((p) => p.project_id !== project.project_id));
    } catch {
      toast.error("Could not delete project");
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(checked);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Delete ${ids.length} project${ids.length === 1 ? "" : "s"}?`,
      description: "This will permanently remove the selected projects and their billing lines. This cannot be undone.",
      confirmLabel: `Delete ${ids.length} project${ids.length === 1 ? "" : "s"}`,
      danger: true,
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      await Promise.all(ids.map((id) => api.delete(`/projects/${id}`)));
      toast.success(`${ids.length} project${ids.length === 1 ? "" : "s"} deleted`);
      setProjects((prev) => prev.filter((p) => !checked.has(p.project_id)));
      setChecked(new Set());
    } catch {
      toast.error("Could not delete every project");
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  // Cross-tab navigation from Billing tab
  useEffect(() => {
    if (openProjectId) {
      setSelectedId(openProjectId);
      onProjectConsumed?.();
    }
  }, [openProjectId, onProjectConsumed, setSelectedId]);

  if (selectedId) {
    return <ProjectDetail projectId={selectedId} onBack={() => setSelectedId(null)} onUpdated={load} />;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="projects-tab">
      <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#4f6fe8]">Database</p>
          <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Projects</h1>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <span className="hidden whitespace-nowrap font-mono text-xs text-white/40 sm:inline">
            {query ? `${filtered.length} of ${projects.length}` : `${projects.length} total`}
          </span>
          <button
            type="button"
            onClick={() => setPeriodOpen(true)}
            data-testid="new-period-btn"
            title="Close a billing period — mark labour and materials as invoiced up to a date"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-3 py-2 text-sm font-semibold text-[#8fa4f0] hover:bg-[#1a3fd1]/25"
          >
            <CalendarRange className="h-4 w-4" /> <span className="hidden sm:inline">New period</span>
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="new-project-btn"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-[#1a3fd1] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1a3fd1]/85"
          >
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New project</span>
          </button>
        </div>
      </div>

      {/* Sticky search — pinned while scrolling the job list so filtering is
          always one tap away (phone especially). bg-[#0a0a0a] is the page
          base; the light-theme remap turns it white. */}
      <div className="sticky top-0 z-30 -mx-4 mb-4 bg-[#0a0a0a] px-4 py-2 sm:mx-0 sm:px-0" data-testid="projects-sticky-search">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects…"
            data-testid="projects-search"
            className="border-white/15 bg-white/[0.03] pl-9 text-white placeholder:text-white/40"
          />
        </div>
      </div>

      {/* Active / Inactive segmented toggle + holdback calendar */}
      {showHoldbacks && <HoldbackCalendar onClose={() => setShowHoldbacks(false)} />}
      <button onClick={() => setShowHoldbacks(true)} className="float-right mb-4 inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-500/20" data-testid="projects-holdbacks-btn">
        <CalendarRange className="h-3.5 w-3.5" /> Holdbacks
      </button>
      <div className="mb-4 inline-flex overflow-hidden rounded-md border border-white/15 bg-white/5" data-testid="projects-status-toggle">
        <button
          type="button"
          onClick={() => setStatusView("active")}
          data-testid="projects-view-active"
          aria-pressed={statusView === "active"}
          className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest transition ${
            statusView === "active" ? "bg-[#1a3fd1] text-white" : "text-white/60 hover:text-white"
          }`}
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Active
          <span className={`ml-1 rounded px-1.5 py-0.5 font-mono text-[9px] ${statusView === "active" ? "bg-white/15" : "bg-white/5"}`}>
            {statusCounts.active}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setStatusView("inactive")}
          data-testid="projects-view-inactive"
          aria-pressed={statusView === "inactive"}
          className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest transition ${
            statusView === "inactive" ? "bg-[#1a3fd1] text-white" : "text-white/60 hover:text-white"
          }`}
        >
          <Archive className="h-3.5 w-3.5" /> Inactive
          <span className={`ml-1 rounded px-1.5 py-0.5 font-mono text-[9px] ${statusView === "inactive" ? "bg-white/15" : "bg-white/5"}`}>
            {statusCounts.inactive}
          </span>
        </button>
      </div>

      {/* Recent jobs — last 3 opened projects, one-tap return (this device) */}
      {!loading && recents.filter((r) => projects.some((p) => p.project_id === r.id)).length > 0 && (
        <div className="mb-3 flex items-center gap-2" data-testid="projects-recents">
          <span className="flex shrink-0 items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-white/40">
            <History className="h-3 w-3" /> Recent
          </span>
          <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto">
            {recents
              .filter((r) => projects.some((p) => p.project_id === r.id))
              .map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedId(r.id)}
                  data-testid={`projects-recent-${r.id}`}
                  title={r.customer ? `${r.name} — ${r.customer}` : r.name}
                  className="max-w-[180px] shrink-0 truncate rounded-full border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-3 py-1 text-[11px] font-medium text-[#8fa4f0] transition-colors hover:border-[#1a3fd1] hover:bg-[#1a3fd1]/20"
                >
                  {r.name}
                </button>
              ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/40">Loading…</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-white/10">
          {/* Bulk action bar — shows selection count + a single button that
              flips selected projects to the opposite status. */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-white/[0.03] px-4 py-1.5" data-testid="projects-bulk-bar">
            <label className="inline-flex items-center gap-2 text-xs text-white/70">
              <input
                type="checkbox"
                checked={filtered.length > 0 && filtered.every((p) => checked.has(p.project_id))}
                ref={(el) => {
                  if (!el) return;
                  const selectedInView = filtered.filter((p) => checked.has(p.project_id)).length;
                  el.indeterminate = selectedInView > 0 && selectedInView < filtered.length;
                }}
                onChange={(e) => {
                  if (e.target.checked) {
                    setChecked(new Set(filtered.map((p) => p.project_id)));
                  } else {
                    setChecked(new Set());
                  }
                }}
                data-testid="projects-select-all"
                className="h-3.5 w-3.5 cursor-pointer accent-[#1a3fd1]"
              />
              <span className="font-mono text-[10px] uppercase tracking-widest">
                {checked.size > 0 ? `${checked.size} selected` : "Select all"}
              </span>
            </label>
            {checked.size > 0 && (
              <div className="flex items-center gap-2">
                {statusView === "active" ? (
                  <button
                    type="button"
                    onClick={() => bulkChangeStatus("Inactive")}
                    disabled={bulkBusy}
                    data-testid="projects-bulk-inactive"
                    className="inline-flex items-center gap-1.5 rounded-md border border-white/20 bg-white/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white hover:border-white/30 hover:bg-white/10 disabled:opacity-50"
                  >
                    <Archive className="h-3 w-3" /> Set inactive
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => bulkChangeStatus("Active")}
                    disabled={bulkBusy}
                    data-testid="projects-bulk-active"
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0] hover:border-[#1a3fd1] hover:bg-[#1a3fd1]/25 disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-3 w-3" /> Set active
                  </button>
                )}
                <button
                  type="button"
                  onClick={bulkDelete}
                  disabled={bulkBusy}
                  data-testid="projects-bulk-delete"
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-red-400 hover:border-red-500 hover:bg-red-500/20 disabled:opacity-50"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            )}
          </div>
          <div className="hidden grid-cols-[20px_minmax(0,2.2fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_96px_110px_28px] items-center gap-3 border-b border-white/10 bg-white/[0.03] px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 md:grid">
            <span />
            <span>Project</span><span>Customer</span><span className="text-center">% Billed</span><span>Status</span><span className="text-right">Value</span><span />
          </div>
          {filtered.map((p, i) => {
            const isSelected = checked.has(p.project_id);
            const norm = normalizedStatus(p.status);
            const accent = norm === "Active" ? "before:bg-[#1a3fd1]" : "before:bg-white/15";
            return (
            <SwipeRow
              key={p.project_id}
              id={p.project_id}
              openId={openSwipeId}
              setOpenId={setOpenSwipeId}
              isActive={norm === "Active"}
              onToggleStatus={() => changeStatus(p, norm === "Active" ? "Inactive" : "Active")}
              onDelete={() => deleteProject(p)}
            >
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, delay: Math.min(i * 0.015, 0.3) }}
              data-testid="project-row"
              className={`group relative grid w-full grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 border-b border-white/[0.05] px-3 py-2 text-left transition-colors before:absolute before:inset-y-0 before:left-0 before:w-[3px] ${accent} before:opacity-0 hover:bg-white/[0.03] hover:before:opacity-100 md:grid-cols-[20px_minmax(0,2.2fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_96px_110px_28px] md:gap-3 md:px-4 md:py-1.5 ${isSelected ? "bg-[#1a3fd1]/[0.08] before:opacity-100" : ""}`}
            >
              <label
                onClick={(ev) => ev.stopPropagation()}
                className="row-span-2 flex cursor-pointer items-center justify-start self-center md:row-span-1 md:justify-center"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(ev) => {
                    setChecked((prev) => {
                      const next = new Set(prev);
                      if (ev.target.checked) next.add(p.project_id);
                      else next.delete(p.project_id);
                      return next;
                    });
                  }}
                  data-testid={`project-check-${p.project_id}`}
                  aria-label={`Select ${p.name}`}
                  className="h-3.5 w-3.5 cursor-pointer accent-[#1a3fd1]"
                />
              </label>
              <button
                type="button"
                onClick={() => setSelectedId(p.project_id)}
                data-testid={`project-open-${p.project_id}`}
                className="col-span-1 flex min-w-0 items-center gap-2 text-left"
              >
                <Layers className="h-3.5 w-3.5 shrink-0 text-[#4f6fe8]" />
                {(p.qbo_po_id || (p.extra_qbo_po_ids || []).length > 0) && (
                  <Link2
                    className="h-3.5 w-3.5 shrink-0 text-green-500"
                    data-testid={`project-qbpo-link-${p.project_id}`}
                    aria-label={`Linked to QuickBooks PO ${[p.qbo_po_id, ...(p.extra_qbo_po_ids || [])].filter(Boolean).join(" + ")}`}
                    title={`Linked to QuickBooks PO: ${[p.qbo_po_id, ...(p.extra_qbo_po_ids || [])].filter(Boolean).join(" + ")}`}
                  />
                )}
                <span className="flex min-w-0 flex-col md:contents">
                  <span className="break-words text-sm font-medium leading-snug text-white/90 md:truncate">{p.name}</span>
                  <span className="truncate font-mono text-[10px] uppercase tracking-widest text-white/40 md:hidden">
                    {(p.customer || p.customer_name || "").trim() || "—"}
                    {p.location ? ` · ${p.location}` : ""}
                  </span>
                </span>
              </button>
              <div className="hidden min-w-0 flex-col text-left text-xs text-white/60 md:flex">
                <button
                  type="button"
                  onClick={() => setSelectedId(p.project_id)}
                  className="truncate text-left hover:text-white/90"
                >
                  {p.customer || p.customer_name || "—"}
                </button>
                {Number(p.qbo_outstanding) > 0 && (
                  <button
                    type="button"
                    onClick={(ev) => { ev.stopPropagation(); openReceivable((p.customer || p.customer_name || "").trim()); }}
                    data-testid={`project-owes-${p.project_id}`}
                    title={`See what ${(p.customer || p.customer_name || "this customer").trim()} owes`}
                    className={`mt-0.5 inline-flex w-fit rounded-full px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider transition-colors ${
                      Number(p.qbo_overdue) > 0 ? "bg-red-500/15 text-red-400 hover:bg-red-500/30" : "bg-amber-500/15 text-amber-400 hover:bg-amber-500/30"
                    }`}
                  >
                    Owes ${Number(p.qbo_outstanding).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedId(p.project_id)}
                className="hidden min-w-0 items-center justify-center gap-1 text-center text-xs md:flex"
                data-testid={`project-billed-${p.project_id}`}
              >
                {(() => {
                  const lines = p.contract_lines || [];
                  const po = lines.reduce((s, l) => s + (Number(l.contract_value) || 0), 0);
                  const vtd = lines.reduce((s, l) => s + (Number(l.value_to_date) || 0), 0);
                  if (po <= 0) return <span className="text-white/30">—</span>;
                  const pct = Math.max(0, Math.min(999, (vtd / po) * 100));
                  const color = pct >= 100 ? "text-emerald-400" : pct >= 50 ? "text-white/80" : "text-white/60";
                  return <span className={`font-mono tabular-nums ${color}`}>{pct.toFixed(pct >= 10 ? 0 : 1)}%</span>;
                })()}
              </button>
              <span className="col-start-3 row-start-2 justify-self-end md:col-start-auto md:row-start-auto md:justify-self-stretch">
                <select
                  value={norm}
                  onChange={(ev) => changeStatus(p, ev.target.value)}
                  onClick={(ev) => ev.stopPropagation()}
                  data-testid={`project-status-${p.project_id}`}
                  className={`w-auto cursor-pointer rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider [color-scheme:dark] focus:outline-none md:w-full md:px-2 md:text-[10px] ${statusStyle(norm)}`}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </span>
              <button
                type="button"
                onClick={() => setSelectedId(p.project_id)}
                className="col-start-3 row-start-1 flex items-center justify-end gap-0.5 self-start pt-0.5 text-right font-mono text-sm font-semibold tabular-nums text-white/90 md:col-start-auto md:row-start-auto md:self-auto md:pt-0 md:text-xs md:font-normal md:text-white/80"
              >
                <DollarSign className="h-3 w-3 text-white/30" />
                {fmt(p.value).replace("$", "")}
              </button>
              <button
                type="button"
                onClick={(ev) => { ev.stopPropagation(); deleteProject(p); }}
                data-testid={`project-delete-${p.project_id}`}
                title="Delete project"
                aria-label={`Delete ${p.name}`}
                className="hidden rounded p-1 text-white/30 opacity-0 transition hover:bg-red-500/10 hover:text-red-300 focus:opacity-100 group-hover:opacity-100 md:inline-flex"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </motion.div>
            </SwipeRow>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-14 text-center text-sm text-white/40">
              {query ? `No projects match "${query}".` : "No projects yet."}
            </div>
          )}
        </div>
      )}

      {/* Create-project dialog */}
      <NewPeriodDialog open={periodOpen} onClose={() => setPeriodOpen(false)} onConfirmed={load} />
      <Dialog open={creating} onOpenChange={(open) => { if (!open) { setCreating(false); setNewForm(emptyNew); } }}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto border-white/10 bg-[#0a0a0a] text-white" data-testid="new-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">New project</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="np-name" className="text-xs text-white/50">Project name *</Label>
              <Input
                id="np-name"
                autoFocus
                data-testid="new-project-name"
                value={newForm.name}
                onChange={(e) => setNewForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. 123 Main St — HVAC replacement"
                className="mt-1 border-white/15 bg-white/[0.03] text-white"
              />
            </div>
            <div>
              <Label htmlFor="np-customer" className="text-xs text-white/50">Customer</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  id="np-customer"
                  data-testid="new-project-customer"
                  value={newForm.customer}
                  onChange={(e) => setNewForm((s) => ({ ...s, customer: e.target.value }))}
                  placeholder="Company / homeowner"
                  className="min-w-0 flex-1 border-white/15 bg-white/[0.03] text-white"
                />
                <CustomerPicker
                  dark
                  testid="new-project-customer-picker"
                  options={customerOpts}
                  onPick={(c) => setNewForm((s) => ({ ...s, customer: c.company_name || c.name || "" }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="np-location" className="text-xs text-white/50">Location</Label>
              <Input
                id="np-location"
                data-testid="new-project-location"
                value={newForm.location}
                onChange={(e) => setNewForm((s) => ({ ...s, location: e.target.value }))}
                placeholder="City / address"
                className="mt-1 border-white/15 bg-white/[0.03] text-white"
              />
            </div>
            <div>
              <Label htmlFor="np-value" className="text-xs text-white/50">PO / Contract value</Label>
              <Input
                id="np-value"
                type="number"
                inputMode="decimal"
                data-testid="new-project-value"
                value={newForm.value}
                onChange={(e) => setNewForm((s) => ({ ...s, value: e.target.value }))}
                placeholder="0.00"
                className="mt-1 border-white/15 bg-white/[0.03] text-white"
              />
            </div>
            <div>
              <Label htmlFor="np-po" className="text-xs text-white/50">Contract / PO #</Label>
              <Input
                id="np-po"
                data-testid="new-project-contract"
                value={newForm.contract_number}
                onChange={(e) => setNewForm((s) => ({ ...s, contract_number: e.target.value }))}
                className="mt-1 border-white/15 bg-white/[0.03] text-white"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-white/50">Type of work</Label>
              <div className="mt-1 space-y-1 rounded-md border border-white/15 bg-white/[0.03] p-2">
                {WORK_TYPES.map((t) => {
                  const checked = newForm.work_types.includes(t);
                  return (
                    <div key={t} className="flex items-center gap-2">
                      <label className="flex flex-1 cursor-pointer items-center gap-2 text-sm text-white">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setNewForm((s) => ({
                            ...s,
                            work_types: e.target.checked ? [...s.work_types, t] : s.work_types.filter((x) => x !== t),
                          }))}
                          data-testid={`new-project-work-${t.replace(/\s+/g, "-").toLowerCase()}`}
                          className="h-3.5 w-3.5 accent-[#1a3fd1]"
                        />
                        {t}
                      </label>
                      {checked && (
                        <Input
                          type="number"
                          inputMode="decimal"
                          placeholder="Value"
                          value={newForm.type_values?.[t] ?? ""}
                          onChange={(e) => setNewForm((s) => ({
                            ...s,
                            type_values: { ...(s.type_values || {}), [t]: e.target.value },
                          }))}
                          data-testid={`new-project-work-value-${t.replace(/\s+/g, "-").toLowerCase()}`}
                          className="h-8 w-28 border-white/15 bg-white/[0.03] text-right text-white"
                        />
                      )}
                    </div>
                  );
                })}
                <p className="text-[10px] text-white/40">Each selected type becomes its own line on the project.</p>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="np-notes" className="text-xs text-white/50">Notes</Label>
              <textarea
                id="np-notes"
                data-testid="new-project-notes"
                value={newForm.notes}
                onChange={(e) => setNewForm((s) => ({ ...s, notes: e.target.value }))}
                rows={2}
                className="mt-1 w-full rounded border border-white/15 bg-white/[0.03] p-2 text-sm text-white outline-none focus:border-[#1a3fd1]"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="np-date" className="text-xs text-white/50">Start date</Label>
              <Input
                id="np-date"
                type="date"
                data-testid="new-project-date"
                value={newForm.date}
                onChange={(e) => setNewForm((s) => ({ ...s, date: e.target.value }))}
                className="mt-1 border-white/15 bg-white/[0.03] text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setCreating(false); setNewForm(emptyNew); }}
              data-testid="new-project-cancel"
              className="text-white/70 hover:text-white"
            >
              <X className="mr-1 h-4 w-4" /> Cancel
            </Button>
            <Button
              type="button"
              onClick={createProject}
              disabled={saving || !newForm.name.trim()}
              data-testid="new-project-save"
              className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
            >
              {saving ? "Creating…" : "Create project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** New period — pick a "billed to" date, tick the jobs with uninvoiced
 * labour/materials up to that date, and confirm to mark them invoiced. */
const NewPeriodDialog = ({ open, onClose, onConfirmed }) => {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState(null); // null = loading
  const [checked, setChecked] = useState({});
  const [confirming, setConfirming] = useState(false);

  const loadRows = useCallback(async (d) => {
    setRows(null);
    try {
      const r = await api.get("/billing/unbilled-to", { params: { date: d } });
      const list = r.data.projects || [];
      setRows(list);
      setChecked(Object.fromEntries(list.map((p) => [p.project_id, true]))); // all ticked by default
    } catch (e) {
      setRows([]);
      toast.error(e?.response?.data?.detail || "Couldn't load uninvoiced jobs");
    }
  }, []);

  useEffect(() => { if (open) loadRows(date); }, [open]); // eslint-disable-line

  const money = (n) => `$${(Number(n) || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const list = rows || [];
  const chosen = list.filter((p) => checked[p.project_id]);
  const allChecked = list.length > 0 && chosen.length === list.length;

  const confirm = async () => {
    if (!chosen.length) return;
    setConfirming(true);
    try {
      const r = await api.post("/billing/new-period", { date, project_ids: chosen.map((p) => p.project_id) });
      const d = r.data;
      toast.success(`Period closed to ${d.date} — ${d.projects.length} job(s): ${d.hours} hrs and ${d.materials_count} material item(s) marked invoiced`);
      onClose();
      onConfirmed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't close the period");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="flex w-[calc(100vw-1.5rem)] flex-col gap-4 overflow-x-hidden border-white/10 bg-[#0a0a0a] text-white sm:max-w-lg" data-testid="new-period-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-black uppercase tracking-tight">New billing period</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50" htmlFor="new-period-date">Billed to date</Label>
            <Input
              id="new-period-date"
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); if (e.target.value) loadRows(e.target.value); }}
              data-testid="new-period-date"
              className="mt-1 border-white/15 bg-white/[0.03] text-white [color-scheme:dark]"
            />
            <p className="mt-1 text-[11px] text-white/40">Every uninvoiced labour row and material up to this date in the ticked jobs gets marked as invoiced.</p>
          </div>

          <div className="rounded-md border border-white/10" data-testid="new-period-list">
            <div className="flex cursor-pointer items-center gap-2 border-b border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white/60"
              onClick={() => setChecked(Object.fromEntries(list.map((p) => [p.project_id, !allChecked])))}
              data-testid="new-period-select-all-row">
              {/* readOnly + pointer-events-none: the row's single onClick is the ONLY toggle path.
                  A <label> wrapping a controlled checkbox double-fires on Android Chrome
                  (native toggle + label-activation click) and silently snaps back — Oct-14 bug */}
              <input
                type="checkbox"
                checked={allChecked}
                readOnly
                tabIndex={-1}
                data-testid="new-period-select-all"
                className="pointer-events-none h-3.5 w-3.5 shrink-0 accent-[#1a3fd1]"
              />
              <span className="min-w-0">Jobs with uninvoiced labour or materials to {date}</span>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {rows === null && <p className="px-3 py-6 text-center text-sm text-white/40">Loading…</p>}
              {rows !== null && !list.length && (
                <p className="px-3 py-6 text-center text-sm text-white/40" data-testid="new-period-empty">Nothing uninvoiced up to that date — every job is billed current.</p>
              )}
              {list.map((p) => (
                <div key={p.project_id} className="flex cursor-pointer items-center gap-3 border-b border-white/5 px-3 py-2 last:border-0 hover:bg-white/[0.03]"
                  onClick={() => setChecked((c) => ({ ...c, [p.project_id]: !c[p.project_id] }))}
                  data-testid={`new-period-row-${p.project_id}`}>
                  <input
                    type="checkbox"
                    checked={!!checked[p.project_id]}
                    readOnly
                    tabIndex={-1}
                    data-testid={`new-period-check-${p.project_id}`}
                    className="pointer-events-none h-3.5 w-3.5 shrink-0 accent-[#1a3fd1]"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-white/90">{p.name}</span>
                    <span className="block font-mono text-[10px] text-white/40">
                      {p.labour_rows > 0 && `${p.hours} hrs labour ${money(p.labour_value)}`}
                      {p.labour_rows > 0 && p.materials_count > 0 && " · "}
                      {p.materials_count > 0 && `${p.materials_count} material${p.materials_count === 1 ? "" : "s"} ${money(p.materials_value)}`}
                    </span>
                  </span>
                  <span className="shrink-0 font-mono text-xs font-bold text-white/80" data-testid={`new-period-total-${p.project_id}`}>{money(p.total_value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="new-period-cancel" className="text-white/70 hover:bg-white/10 hover:text-white">Cancel</Button>
          <Button onClick={confirm} disabled={confirming || !chosen.length} data-testid="new-period-confirm" className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85">
            {confirming ? "Marking…" : `Mark invoiced (${chosen.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectsTab;
